import { HttpClient, HttpRequestOptions, HttpResponse } from '../../utils/ajax';

type VotingApiRequestBody = Record<string, string> | URLSearchParams;

type ApiErrorPayload = {
  success: false;
  data?: {
    message?: string;
    error_code?: string;
  };
};

type SubmitVoteSuccessPayload = {
  success: true;
  data: {
    updated_results?: unknown;
    votes_remaining?: number;
    message?: string;
  };
};

type VerifyCpfSuccessPayload = {
  success: true;
  data: {
    status: 'verified' | 'pending' | 'rejected';
    message?: string;
    votes_remaining?: number;
  };
};

type ResultsSuccessPayload<T> = {
  success: true;
  data: T;
};

export interface SubmitVoteResponseData {
  votesRemaining?: number;
  updatedResults?: unknown;
  message?: string;
}

export interface VerifyCpfResponseData {
  status: 'verified' | 'pending' | 'rejected';
  message?: string;
  votesRemaining?: number;
}

export interface PerformanceResultsDto {
  // Estrutura crua retornada pelo backend legado; será refinada na camada de transformação.
  [key: string]: unknown;
}

export interface BatchResultsDto {
  // Estrutura crua retornada pelo backend legado; será refinada na camada de transformação.
  [key: string]: unknown;
}

export interface BatchResultsRequestPayload {
  performanceIds: string[];
  autoRefresh?: boolean;
}

export interface LoadPerformancesDto {
  html: string;
  [key: string]: unknown;
}

export interface SubmitVoteParams {
  performanceId: string;
  value: string;
  votingId: string;
  voterCpf: string;
  nonce: string;
  csrfToken?: string;
  timestamp: string;
}

export interface VerifyCpfParams {
  votingId: string;
  cpf: string;
  nonce: string;
  csrfToken?: string;
}

export interface ResultsParams {
  performanceId: string;
  votingId: string;
  nonce: string;
}

export interface BatchResultsParams {
  votingId: string;
  nonce: string;
  performanceIds: string[];
  autoRefresh?: boolean;
}

export interface LoadPerformancesParams {
  votingId: string;
  category?: string;
  nonce: string;
}

export class VotingApi {
  constructor(private readonly client: HttpClient) {}

  async submitVote(params: SubmitVoteParams, options?: HttpRequestOptions<VotingApiRequestBody>): Promise<SubmitVoteResponseData> {
    const response = await this.client.request<VotingApiRequestBody, SubmitVoteSuccessPayload | ApiErrorPayload>('', {
      ...options,
      method: 'POST',
      body: {
        action: 'submit_vote',
        performance_id: params.performanceId,
        vote_value: params.value,
        voting_id: params.votingId,
        voter_cpf: params.voterCpf,
        nonce: params.nonce,
        csrf_token: params.csrfToken ?? '',
        timestamp: params.timestamp
      }
    });

    return this.normalizeSubmitVoteResponse(response);
  }

  async verifyCpf(params: VerifyCpfParams, options?: HttpRequestOptions<VotingApiRequestBody>): Promise<VerifyCpfResponseData> {
    const response = await this.client.request<VotingApiRequestBody, VerifyCpfSuccessPayload | ApiErrorPayload>('', {
      ...options,
      method: 'POST',
      body: {
        action: 'verify_cpf',
        voting_id: params.votingId,
        cpf: params.cpf,
        nonce: params.nonce,
        csrf_token: params.csrfToken ?? ''
      }
    });

    return this.normalizeVerifyCpfResponse(response);
  }

  async getPerformanceResults(params: ResultsParams, options?: HttpRequestOptions<VotingApiRequestBody>): Promise<PerformanceResultsDto> {
    const response = await this.client.request<VotingApiRequestBody, ResultsSuccessPayload<PerformanceResultsDto> | ApiErrorPayload>('', {
      ...options,
      method: 'POST',
      body: {
        action: 'get_performance_results',
        performance_id: params.performanceId,
        voting_id: params.votingId,
        nonce: params.nonce
      }
    });

    return this.normalizeResultsResponse(response);
  }

  async getBatchResults(params: BatchResultsParams, options?: HttpRequestOptions<VotingApiRequestBody>): Promise<BatchResultsDto> {
    const body = new URLSearchParams({
      action: 'get_all_performance_results',
      voting_id: params.votingId,
      nonce: params.nonce,
      auto_refresh: params.autoRefresh ? '1' : '0'
    });

    params.performanceIds.forEach((id, index) => {
      body.append(`performance_ids[${index}]`, id);
    });

    const response = await this.client.request<VotingApiRequestBody, ResultsSuccessPayload<BatchResultsDto> | ApiErrorPayload>('', {
      ...options,
      method: 'POST',
      body
    });

    return this.normalizeResultsResponse(response);
  }

  async loadPerformances(params: LoadPerformancesParams, options?: HttpRequestOptions<VotingApiRequestBody>): Promise<LoadPerformancesDto> {
    const response = await this.client.request<VotingApiRequestBody, ResultsSuccessPayload<LoadPerformancesDto> | ApiErrorPayload>('', {
      ...options,
      method: 'POST',
      body: {
        action: 'load_performances',
        voting_id: params.votingId,
        category: params.category ?? '',
        nonce: params.nonce
      }
    });

    return this.normalizeLoadPerformancesResponse(response);
  }

  private normalizeSubmitVoteResponse(response: HttpResponse<SubmitVoteSuccessPayload | ApiErrorPayload>): SubmitVoteResponseData {
    if (!response.data.success) {
      const message = response.data.data?.message ?? 'Erro ao registrar voto.';
      throw new Error(message);
    }

    const payload = response.data as SubmitVoteSuccessPayload;

    return {
      votesRemaining: payload.data?.votes_remaining,
      updatedResults: payload.data?.updated_results,
      message: payload.data?.message
    };
  }

  private normalizeVerifyCpfResponse(response: HttpResponse<VerifyCpfSuccessPayload | ApiErrorPayload>): VerifyCpfResponseData {
    if (!response.data.success) {
      const message = response.data.data?.message ?? 'Erro ao verificar CPF.';
      throw new Error(message);
    }

    const payload = response.data as VerifyCpfSuccessPayload;

    return {
      status: payload.data?.status ?? 'pending',
      message: payload.data?.message,
      votesRemaining: payload.data?.votes_remaining
    };
  }

  private normalizeResultsResponse<T>(response: HttpResponse<ResultsSuccessPayload<T> | ApiErrorPayload>): T {
    if (!response.data.success) {
      const message = response.data.data?.message ?? 'Erro ao buscar resultados.';
      throw new Error(message);
    }

    return (response.data as ResultsSuccessPayload<T>).data;
  }

  private normalizeLoadPerformancesResponse(response: HttpResponse<ResultsSuccessPayload<LoadPerformancesDto> | ApiErrorPayload>): LoadPerformancesDto {
    if (!response.data.success) {
      const message = response.data.data?.message ?? 'Erro ao carregar performances.';
      throw new Error(message);
    }

    const payload = response.data as ResultsSuccessPayload<LoadPerformancesDto>;

    if (!payload.data?.html) {
      throw new Error('Resposta inválida ao carregar performances.');
    }

    return payload.data;
  }
}
